"use client";

import { useEffect, useState } from "react";
import Image from "next/image";

import {
  Minus,
  Plus,
  ShoppingCart,
} from "lucide-react";

import {
  useAppDispatch,
  useAppSelector,
} from "@/redux/hook";

import {
  add_cart_product,
  quantityDecrement,
  remove_product,
  setCartQuantity,
} from "@/redux/features/cart";

import styles from "./GadgetProductCard.module.css";

type Props = {
  product: any;
  setLoading?: (loading: boolean) => void;
};

export default function GadgetProductCard({
  product,
  setLoading,
}: Props) {
  const dispatch = useAppDispatch();

  /* =====================================================
     PRODUCT ATTRIBUTE
  ===================================================== */

  const attr =
    product?.productAttributes?.[0];

  const discountPercent = Number(
    attr?.discountPercent ?? 0
  );

  const stock = Number(
    attr?.stockAmount ?? 0
  );

  const retailPrice = Number(
    attr?.retailPrice ?? 0
  );

  const discountedRetailPrice =
    Number(
      attr?.discountedRetailPrice ?? 0
    );

  /* =====================================================
     DISCOUNT
  ===================================================== */

  const hasDiscount =
    discountPercent > 0 &&
    discountedRetailPrice > 0 &&
    discountedRetailPrice <
      retailPrice;

  const salePrice = hasDiscount
    ? discountedRetailPrice
    : retailPrice;

  /* =====================================================
     PRODUCT INFO
  ===================================================== */

  const image =
    product?.images?.[0]?.image ||
    "/noimage.png";

  const brandName =
    product?.brand?.name ||
    product?.brandName ||
    "General";

  const variant =
    attr?.size ?? "";

  const productAttributeId =
    attr?.id ?? "";

  /* =====================================================
     CART PRODUCT ID
  ===================================================== */

  const cartProductId =
    String(product?.id ?? "") +
    String(
      variant ||
        productAttributeId ||
        ""
    );

  /* =====================================================
     CURRENT CART ITEM
  ===================================================== */

  const cartItem = useAppSelector(
    (state) =>
      state.cart.cart_products.find(
        (item: any) =>
          String(item.id) ===
          String(cartProductId)
      )
  );

  const quantity = Number(
    cartItem?.orderQuantity ?? 0
  );

  const isInCart =
    quantity > 0;

  /* =====================================================
     LOCAL INPUT QUANTITY
     
     এটি ব্যবহার করার কারণে input freely type করা যাবে।
     Redux state change হলেও input sync থাকবে।
  ===================================================== */

  const [inputQuantity, setInputQuantity] =
    useState(
      quantity > 0
        ? String(quantity)
        : ""
    );

  useEffect(() => {
    setInputQuantity(
      quantity > 0
        ? String(quantity)
        : ""
    );
  }, [quantity]);

  /* =====================================================
     NAVIGATE TO PRODUCT DETAILS
  ===================================================== */

  const handleNavigateToDetails =
    () => {
      if (!product?.slug) {
        return;
      }

      setLoading?.(true);

      window.location.href =
        `/product-details/${product.slug}`;
    };

  /* =====================================================
     CART DATA
  ===================================================== */

  const cartProductData = {
    name: product?.name,

    image,

    id: cartProductId,

    variant,

    orderQuantity: 1,

    discountedRetailPrice:
      salePrice,

    costPrice: Number(
      attr?.costPrice ?? 0
    ),

    retailPrice,

    discountPrice: hasDiscount
      ? Number(
          attr?.discountPrice ?? 0
        )
      : 0,

    discountPercent:
      hasDiscount
        ? discountPercent
        : 0,

    totalCostPrice: Number(
      attr?.costPrice ?? 0
    ),

    totalPrice: salePrice,

    productId: product?.id,

    productAttributeId,
  };

  /* =====================================================
     ADD TO CART
  ===================================================== */

  const handleAddToCart = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();

    if (stock <= 0) {
      return;
    }

    dispatch(
      add_cart_product(
        cartProductData
      )
    );
  };

  /* =====================================================
     INCREMENT
  ===================================================== */

  const handleIncrement = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();

    if (quantity >= stock) {
      return;
    }

    dispatch(
      add_cart_product(
        cartProductData
      )
    );
  };

  /* =====================================================
     DECREMENT

     3 -> 2
     2 -> 1
     1 -> REMOVE
  ===================================================== */

  const handleDecrement = (
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation();

    if (!cartItem) {
      return;
    }

    const currentQuantity =
      Number(
        cartItem.orderQuantity
      ) || 0;

    if (currentQuantity <= 0) {
      return;
    }

    /* ================================================
       QUANTITY 1 হলে REMOVE PRODUCT
    ================================================= */

    if (currentQuantity === 1) {
      dispatch(
        remove_product({
          id: cartItem.id,
          title: cartItem.name,
        })
      );

      return;
    }

    /* ================================================
       QUANTITY 2+ হলে DECREMENT
    ================================================= */

    dispatch(
      quantityDecrement({
        id: cartItem.id,
      })
    );
  };

  /* =====================================================
     DIRECT QUANTITY INPUT

     এখানে user freely typing করতে পারবে।
     যেমন:
     1 delete করে 2
     1 delete করে 10
     5 থেকে 25
  ===================================================== */

  const handleQuantityChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();

    const value =
      e.target.value;

    /*
     * শুধু digit অথবা empty allow করবে
     */

    if (!/^\d*$/.test(value)) {
      return;
    }

    /*
     * প্রথমে local state update হবে
     * তাই user freely type করতে পারবে
     */

    setInputQuantity(value);

    /*
     * Empty input allow
     */

    if (value === "") {
      return;
    }

    if (!cartItem) {
      return;
    }

    let finalQuantity =
      Number(value);

    if (
      !Number.isFinite(
        finalQuantity
      )
    ) {
      return;
    }

    /*
     * Decimal allow করবে না
     */

    finalQuantity =
      Math.floor(finalQuantity);

    /*
     * 0 দিলে product remove হবে
     */

    if (finalQuantity <= 0) {
      dispatch(
        remove_product({
          id: cartItem.id,
          title: cartItem.name,
        })
      );

      return;
    }

    /*
     * Stock-এর বেশি হলে
     * stock পর্যন্ত রাখবে
     */

    if (
      finalQuantity > stock
    ) {
      finalQuantity = stock;

      setInputQuantity(
        String(stock)
      );
    }

    dispatch(
      setCartQuantity({
        id: cartItem.id,
        quantity: finalQuantity,
      })
    );
  };

  /* =====================================================
     QUANTITY INPUT BLUR
  ===================================================== */

  const handleQuantityBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();

    if (!cartItem) {
      return;
    }

    const value =
      inputQuantity;

    /*
     * Empty হলে আবার 1 হবে
     */

    if (value === "") {
      setInputQuantity("1");

      dispatch(
        setCartQuantity({
          id: cartItem.id,
          quantity: 1,
        })
      );

      return;
    }

    let finalQuantity =
      Number(value);

    /*
     * Invalid অথবা 0 হলে 1 হবে
     */

    if (
      !Number.isFinite(
        finalQuantity
      ) ||
      finalQuantity <= 0
    ) {
      finalQuantity = 1;
    }

    /*
     * শুধু integer
     */

    finalQuantity =
      Math.floor(finalQuantity);

    /*
     * Stock-এর বেশি হলে stock
     */

    if (
      finalQuantity > stock
    ) {
      finalQuantity = stock;
    }

    /*
     * Input এবং Redux দুটোই sync
     */

    setInputQuantity(
      String(finalQuantity)
    );

    dispatch(
      setCartQuantity({
        id: cartItem.id,
        quantity: finalQuantity,
      })
    );
  };

  /* =====================================================
     QUANTITY INPUT KEY
  ===================================================== */

  const handleQuantityKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    e.stopPropagation();

    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
  };

  /* =====================================================
     CARD KEYBOARD
  ===================================================== */

  const handleCardKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (
      e.key === "Enter" ||
      e.key === " "
    ) {
      e.preventDefault();

      handleNavigateToDetails();
    }
  };

  return (
    <div
      className={styles.card}
      onClick={
        handleNavigateToDetails
      }
      onKeyDown={
        handleCardKeyDown
      }
      role="link"
      tabIndex={0}
    >
      {/* =================================================
          IMAGE
      ================================================= */}

      <div
        className={
          styles.imageWrap
        }
      >
        <Image
          src={image}
          alt={
            product?.name ||
            "Product"
          }
          fill
          sizes="
            (max-width: 480px) 50vw,
            (max-width: 767px) 33vw,
            (max-width: 1200px) 20vw,
            180px
          "
          className={
            styles.image
          }
          loading="lazy"
        />

        {hasDiscount && (
          <div
            className={
              styles.discountBadge
            }
          >
            -{discountPercent}%
          </div>
        )}
      </div>

      {/* =================================================
          INFO
      ================================================= */}

      <div
        className={
          styles.info
        }
      >
        {/* PRODUCT NAME */}

        <h3
          className={
            styles.name
          }
        >
          {product?.name}
        </h3>

        {/* BRAND */}

        <div
          className={
            styles.brandName
          }
        >
          {brandName}
        </div>

        {/* PRICE */}

        <div
          className={
            styles.priceRow
          }
        >
          {hasDiscount && (
            <span
              className={
                styles.oldPrice
              }
            >
              ৳{" "}
              {retailPrice.toLocaleString(
                "en-BD"
              )}
            </span>
          )}

          <span
            className={
              styles.newPrice
            }
          >
            ৳{" "}
            {salePrice.toLocaleString(
              "en-BD"
            )}
          </span>
        </div>

        {/* =================================================
            CART BUTTON
        ================================================= */}

        {stock <= 0 ? (
          <button
            type="button"
            className={
              styles.outOfStockBtn
            }
            disabled
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            Out of Stock
          </button>
        ) : !isInCart ? (
          <button
            type="button"
            className={
              styles.addCartBtn
            }
            onClick={
              handleAddToCart
            }
          >
            <ShoppingCart
              size={16}
              strokeWidth={2.4}
            />

            <span>
              Add To Cart
            </span>
          </button>
        ) : (
          <div
            className={
              styles.quantityControl
            }
            onClick={(e) =>
              e.stopPropagation()
            }
          >
            {/* MINUS */}

            <button
              type="button"
              className={
                styles.quantityBtn
              }
              onClick={
                handleDecrement
              }
              aria-label="Decrease quantity"
            >
              <Minus
                size={16}
                strokeWidth={2.8}
              />
            </button>

            {/* DIRECT QUANTITY INPUT */}

            <input
              type="text"
              inputMode="numeric"
              value={inputQuantity}
              className={
                styles.quantityInput
              }
              onChange={
                handleQuantityChange
              }
              onBlur={
                handleQuantityBlur
              }
              onClick={(e) =>
                e.stopPropagation()
              }
              onKeyDown={
                handleQuantityKeyDown
              }
              aria-label="Product quantity"
            />

            {/* PLUS */}

            <button
              type="button"
              className={
                styles.quantityBtn
              }
              onClick={
                handleIncrement
              }
              disabled={
                quantity >= stock
              }
              aria-label="Increase quantity"
            >
              <Plus
                size={16}
                strokeWidth={2.8}
              />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}